<template>
	<button
		type="button"
		@click="isSelectable && $emit('toggle')"
		:disabled="!isSelectable"
		:class="[
			'bg-white rounded-[14px] flex flex-col md:flex-row delay-000 duration-100 transition border text-left w-full',
			isSelectable ? 'hover:opacity-80 cursor-pointer' : 'cursor-not-allowed',
			selected ? 'border-primary ring-3 ring-primary' : 'border-surface-grey',
			inView ? (isSelectable ? 'opacity-100 translate-y-0' : 'opacity-60 translate-y-0') : 'opacity-0 translate-y-4',
		]"
	>
		<div ref="inviewRef" class="p-4 grow min-w-0">
			<div v-if="badges[0]" class="mb-3">
				<span :class="'text-headings text-xs px-2 py-1 rounded font-semibold ' + badges[0].class">
					<template v-if="badges[0].localeKey">
						{{ $t(badges[0].localeKey) }}
					</template>
					<template v-else>
						{{ badges[0].string || badges[0] }}
					</template>
				</span>
			</div>
			<div class="md:flex md:justify-between md:mb-3">
				<div class="md:flex-1 min-w-0">
					<h2 class="h4 mb-2">
						{{ getLocalizedEventValue(eventItem, locale, 'info1') }}
					</h2>
					<p
						v-if="eventItem.doorsale_note"
						class="text-text text-xs mb-3 whitespace-pre-line"
					>
						{{ eventItem.doorsale_note }}
					</p>
				</div>
				<div class="md:text-right md:ml-4 flex-shrink-0">
					<div class="flex items-center gap-2 mb-2 md:justify-end md:flex-col md:items-end">
						<span class="text-lg font-bold text-headings">
							{{ eventItem.price_override_active ? getLocalizedEventValue(eventItem, locale, 'price_override_text') : formatPrice(eventItem.price, eventItem.currency, $i18n.locale) }}
						</span>
					</div>
				</div>
			</div>
			<div class="flex items-center text-sm text-headings mb-2">
				<CalendarIcon class="h-4 w-4 mr-2 fill-surface-dark flex-shrink-0" />
				<EventDate :event="eventItem" class="line-clamp-1" />
			</div>
			<div class="flex items-center text-sm text-headings mb-2">
				<MapPinIcon class="h-4 w-4 mr-2 fill-surface-dark flex-shrink-0" />
				<span class="line-clamp-1">{{ venue ? venue.name : '' }}</span>
			</div>

		</div>
	</button>
</template>

<script setup lang="ts">
	import { computed } from 'vue';
	import { useI18n } from 'vue-i18n';

	import EventDate from '@/components/event/EventDate.vue';
	import CalendarIcon from '@/components/icons/CalendarIcon.vue';
	import MapPinIcon from '@/components/icons/MapPinIcon.vue';
	import { useGeneralStore } from '@/stores/general';
	import type { DoorsaleEvent } from '@/types';
	import { getBadgesForEvent, getLocalizedEventValue } from '@/utils/eventItemUtil';
	import { formatPrice } from '@/utils/formatPrice';
	import { useInView } from '@/utils/useInView';

	const { locale, t: $t } = useI18n();

	const props = defineProps<{
		eventItem: DoorsaleEvent;
		selected: boolean;
	}>();

	defineEmits<{
		toggle: [];
	}>();

	const { inviewRef, inView } = useInView();

	const generalStore = useGeneralStore();

	const venues = computed(() => generalStore.venues());

	const venue = computed(() =>
		venues.value?.find(v => v.id === props.eventItem.venue)
	);

	const badges = computed(() => getBadgesForEvent(props.eventItem));

	const isSelectable = computed(() => !props.eventItem.soldout);
</script>
